import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ClientLayout } from '../../../components/layout/ClientLayout';
import { useCheckPermissions, checkContestPermission } from '../../../middleware/checkPermissions';
import { supabase } from '../../../lib/supabase';
import { Loader2, Users, Vote, DollarSign, Clock } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface ContestData {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  vote_price: number;
  votes_count: number;
  photo_url: string;
}

interface VoteData {
  date: string;
  votes: number;
  amount: number;
}

export const ContestDetails = () => {
  const { contestId } = useParams<{ contestId: string }>();
  const { user } = useCheckPermissions(['view_stats']);
  const [contest, setContest] = useState<ContestData | null>(null);
  const [voteHistory, setVoteHistory] = useState<VoteData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContestDetails = async () => {
      if (!user || !contestId || !checkContestPermission(user, contestId, 'view')) return;

      try {
        setIsLoading(true);

        // Récupérer les détails du concours
        const { data: contestData, error: contestError } = await supabase
          .from('contests')
          .select(`
            *,
            contestants (count),
            votes (count)
          `)
          .eq('id', contestId)
          .single();

        if (contestError) throw contestError;

        // Récupérer l'historique des votes
        const { data: voteData, error: voteError } = await supabase
          .from('votes')
          .select('created_at, amount')
          .eq('contest_id', contestId)
          .order('created_at', { ascending: true });

        if (voteError) throw voteError;

        // Formater l'historique des votes par jour
        const voteHistory = voteData.reduce((acc: Record<string, VoteData>, vote: any) => {
          const date = new Date(vote.created_at).toISOString().split('T')[0];
          if (!acc[date]) {
            acc[date] = { date, votes: 0, amount: 0 };
          }
          acc[date].votes += 1;
          acc[date].amount += vote.amount;
          return acc;
        }, {});

        setContest(contestData);
        setVoteHistory(Object.values(voteHistory));
      } catch (error) {
        console.error('Erreur lors du chargement des détails du concours:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContestDetails();
  }, [user, contestId]);

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
        </div>
      </ClientLayout>
    );
  }

  if (!contest) {
    return (
      <ClientLayout>
        <div className="text-center text-gray-400">
          Concours non trouvé ou accès non autorisé.
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">{contest.name}</h2>
          <p className="text-gray-400">{contest.description}</p>
        </div>

        {/* Statistiques principales */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/10">
            <div className="flex items-center gap-4">
              <Users className="h-8 w-8 text-yellow-400" />
              <div>
                <p className="text-sm text-gray-400">Candidats</p>
                <p className="text-2xl font-bold text-white">
                  {contest.contestants?.count || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/10">
            <div className="flex items-center gap-4">
              <Vote className="h-8 w-8 text-yellow-400" />
              <div>
                <p className="text-sm text-gray-400">Votes</p>
                <p className="text-2xl font-bold text-white">
                  {contest.votes?.count || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/10">
            <div className="flex items-center gap-4">
              <DollarSign className="h-8 w-8 text-yellow-400" />
              <div>
                <p className="text-sm text-gray-400">Prix du vote</p>
                <p className="text-2xl font-bold text-white">
                  {new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'XAF'
                  }).format(contest.vote_price)}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/10">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8 text-yellow-400" />
              <div>
                <p className="text-sm text-gray-400">Statut</p>
                <p className="text-2xl font-bold text-white capitalize">
                  {contest.status}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Graphique des votes */}
        {voteHistory.length > 0 && (
          <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-6">Évolution des votes</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={voteHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="date"
                    stroke="#9CA3AF"
                    tick={{ fill: '#9CA3AF' }}
                  />
                  <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#F3F4F6'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="votes"
                    stroke="#EAB308"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ContestDetails;
